import { NgClass } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, inject, signal } from "@angular/core";
import { MatToolbar } from "@angular/material/toolbar";
import { SafeUrl, Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { IconButtonComponent } from "@tylertech/forge";
import { ForgeBannerModule, ForgeButtonModule, ForgeIconButtonModule, ForgeInlineMessageModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor } from "@tylertech/logging";
import { LOCATION, Utilities, WINDOW } from "@tylertech/shared-module";
import { IAppRegistrationModel, Key, ModifierKey } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FeatureFlagHelperService, FeatureFlags, KeyHandlerService, NavMenuService, NavigationService, OmniBarService, OmniColorClass, RouteInfoService, SecurityService } from "../../services";
import { HelpMenuComponent } from "./helpMenu/helpMenu.component";
import { ConnGroupActionComponent } from "./nineBox/connGroupAction.component";
import { NineBoxComponent } from "./nineBox/nineBox.component";
import { OmniSearchComponent } from "./omniSearch/omniSearch.component";
import { ProfileMenuComponent } from "./profileMenu/profileMenu.component";
import { SmartPulseComponent } from "./smartPulse";
import { SupportChatComponent } from "./supportChat/supportChat.component";

@Component({
    selector: "omni",
    templateUrl: "./omniBar.tmpl.html",
    styleUrls: [
        "./omniBar.component.scss"
    ],
    imports: [
        ClassDirective,
        ConnGroupActionComponent,
        ForgeBannerModule,
        ForgeButtonModule,
        ForgeIconButtonModule,
        ForgeInlineMessageModule,
        ForgeTooltipModule,
        HelpMenuComponent,
        MatToolbar,
        NgClass,
        NineBoxComponent,
        OmniSearchComponent,
        ProfileMenuComponent,
        SmartPulseComponent,
        SupportChatComponent,
    ]
})
export class OmniBarComponent implements OnDestroy, OnInit
{
    private readonly $routeInfoService = inject(RouteInfoService);
    private readonly window = inject<globalThis.Window>(WINDOW);
    private readonly location = inject<Location>(LOCATION);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly router = inject(Router);
    private readonly $titleService = inject(Title);
    private readonly $navMenuService = inject(NavMenuService);
    private readonly $omniBarService = inject(OmniBarService);
    private readonly $securityService = inject(SecurityService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $shellService = inject(ShellService);
    private readonly $featureFlagHelperService = inject(FeatureFlagHelperService);

    /** @deprecated - The context for this functionality should be inverted. Boolean states should never default to `true` */
    @Input() readonly backBar: boolean;
    @Input() readonly commandKey: string;
    @Input() readonly rootApp: string;

    @ViewChild("omnibarMenu", { read: ElementRef }) readonly omnibarMenuRef: ElementRef<IconButtonComponent>;

    private readonly destructor$ = new Subject<void>();
    protected readonly showTylerAssistantFeatureFlag = this.$featureFlagHelperService.get(FeatureFlags.Shared_All_SmartPulseAssistant);

    private listenerId: string;
    private readonly log = getLoggerFor("OmniBarComponent");

    apps: IAppRegistrationModel[] = [];
    brand: string;
    embedded: boolean;
    menuDisabled: boolean = true;
    authenticated: boolean;
    isVendorUser: boolean;
    omniTitle = signal("");
    defaultAppUrl: SafeUrl;

    constructor()
    {
        this.embedded = this.$shellService.isEmbedded();

        if (this.$securityService.hasSetting("OmniBarColor"))
        {
            this.$omniBarService.colorClass = this.$securityService.Settings.OmniBarColor as OmniColorClass;
        }
    }

    private setupObservables(): void
    {
        const self = this;

        this.$navMenuService.disabled
            .pipe(takeUntil(this.destructor$))
            .subscribe((disabled) =>
            {
                this.menuDisabled = disabled;
            });

        this.$navMenuService.toggleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe((open: boolean) =>
            {
                if (!open)
                {
                    this.omnibarMenuRef?.nativeElement?.focus(undefined);
                }
            });

        this.$securityService.principleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe((newPrinciple) =>
            {
                this.log.debug(`SecurityService.principleChange`, { newPrinciple });
                this.authenticated = this.$securityService.isAuthenticated;
                this.isVendorUser = this.$securityService.isVendorUser;
                updateTitle(this.$omniBarService.title);
            });

        this.$omniBarService.titleChange
            .pipe(takeUntil(this.destructor$))
            .subscribe(newTitle =>
            {
                this.log.debug(`OmniBarService.titleChange`, { newTitle });
                updateTitle(newTitle);
            });

        function updateTitle(newTitle: string): void
        {
            let omniTitle = self.authenticated
                ? (newTitle ? newTitle : self.$titleService.getTitle())
                : "";
            if (omniTitle === "Login")
                omniTitle = "";
            self.omniTitle.set(omniTitle);
        }
    }

    get isProvisional(): boolean
    {
        return this.$securityService.isProvisional;
    }

    get isFeatureFlagged(): boolean
    {
        return this.$featureFlagHelperService.isDevFlagOn;
    }

    get isEmbedded(): boolean
    {
        return this.embedded;
    }

    get showBackBar(): boolean
    {
        return this.backBar ?? true;
    }

    get showERPProBanner(): boolean
    {
        // Show it in WebLauncher only
        if (!window["webLauncher"])
            return false;

        // Don't show it in express-int, since download isn't supported there
        const domain = this.$routeInfoService.domain.toLowerCase();

        return domain.indexOf("/express-int.tylerhost.net") === -1 &&
            domain.indexOf("/express-prod.tylerhost.net") === -1;
    }

    get getERPProDownloadUrl(): string
    {
        return "/ERPProApp/setup.exe?Tenant=" + this.$routeInfoService.tenant.toLowerCase();
    }

    get omniBarClasses(): Record<string, boolean>
    {
        const classes = {};

        const className = `app-bar--${this.$omniBarService.colorClass}`;
        classes[className] = true;

        return classes;
    }

    get allowSearch(): boolean
    {
        return !this.isVendorUser;
    }

    @HostListener("window:keydown", ["$event"])
    onKeydown($event: KeyboardEvent): void
    {
        this.$keyHandlerService.handleKeyDown($event);
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
        this.$keyHandlerService.unsubscribe(Key.F2, ModifierKey.Ctrl);
    }

    ngOnInit(): void
    {
        this.setupObservables();
        this.listenerId = Utilities.generateRandomGuid();
        // TODO(DO-4549) - convert to hostListener
        this.$keyHandlerService.subscribe(
            this.listenerId,
            Key.F2,
            ModifierKey.Ctrl,
            "Global|Open/close navigation menu",
            () =>
            {
                if (!this.menuDisabled)
                {
                    this.$navMenuService.toggle();
                }
            }
        );
        this.setDefaultAppUrl();
    }

    back(): void
    {
        this.window.history.back();
    }

    setDefaultAppUrl(): void
    {
        // TODO - shouldn't we rely on the backend to tell us what the default app for the current user is?
        let app = "dashboard";
        if (!this.$securityService.principle)
        {
            this.defaultAppUrl = "";
            return;
        }

        if (this.$securityService.isVendorUser)
        {
            app = "vendormanager";
        }

        if (this.$shellService.isEmbedded())
        {
            // TODO - why do we need to add a route param to detect the embedded state?
            const embedded = "/1";
            app = app + embedded;
        }

        this.defaultAppUrl = this.$navigationService.getLinkToApp(app);
    }

    openLearnMoreERPPro(): void
    {
        window.open("https://tylertech.csod.com/samldefault.aspx?ReturnUrl=https://tylertech.csod.com/ui/lms-learning-details/app/material/81e6800a-719a-4b71-93e1-11fad3b1f6c9", "_blank");
    }

    reload(): void
    {
        if (this.rootApp)
        {
            this.router.navigateByUrl(this.$navigationService.getLinkToApp(this.rootApp) as string);
            return;
        }

        this.location.reload();
    }

    toggleMenu(): void
    {
        this.$navMenuService.toggle();
    }
}
